import { useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import AskAndyChip from "@/components/chat/AskAndyChip";
import {
  Sparkles,
  Apple,
  Pill,
  Stethoscope,
  Ban,
} from "lucide-react";

const SECTION_CONFIG = [
  { key: "supplementation", icon: Pill, chip: "bg-amber-50 text-amber-900 border-amber-100", label: "bg-amber-100 text-amber-800" },
  { key: "clinical", icon: Stethoscope, chip: "bg-sky-50 text-sky-900 border-sky-100", label: "bg-sky-100 text-sky-800" },
  { key: "nutrition", icon: Apple, chip: "bg-emerald-50 text-emerald-900 border-emerald-100", label: "bg-emerald-100 text-emerald-800" },
  { key: "lifestyle", icon: Sparkles, chip: "bg-violet-50 text-violet-900 border-violet-100", label: "bg-violet-100 text-violet-800" },
  { key: "exclusion", icon: Ban, chip: "bg-rose-50 text-rose-900 border-rose-100", label: "bg-rose-100 text-rose-800" },
];

export default function AlgorithmCard({ id, algo, idx = 0 }) {
  const { t } = useLang();

  if (!algo || !algo.SUCCESS) return null;
  const title = t.results.algoNames[id] || id;

  const sections = SECTION_CONFIG.map((sec) => ({
    ...sec,
    items: Array.isArray(algo[sec.key]) ? algo[sec.key].filter(Boolean) : [],
  })).filter((sec) => sec.items.length > 0);

  const recCount = sections.reduce((sum, sec) => sum + sec.items.length, 0);

  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.24) }}
      className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_12px_40px_-28px_rgba(81,75,186,0.35)]"
      data-testid={`algorithm-card-${id}`}
    >
      <div className="border-b border-zinc-100 bg-gradient-to-r from-[color:var(--sentinel-tint)]/40 via-white to-white p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex shrink-0 rounded-full bg-[color:var(--sentinel)] px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
                {algo.response || "—"}
              </span>
              <h3 className="text-base font-semibold text-zinc-900 md:text-lg">{title}</h3>
              {recCount > 0 && (
                <span className="inline-flex shrink-0 rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-zinc-500">
                  {recCount}
                </span>
              )}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-zinc-600 md:text-sm">
              {algo.description}
            </p>
          </div>
          <AskAndyChip
            size="xs"
            prefill={t.chat.prefill.algorithm
              .replace("{name}", title)
              .replace("{code}", algo.response || "—")}
            testId={`ask-andy-algorithm-${id}`}
          />
        </div>
      </div>

      {sections.length > 0 ? (
        <div className="space-y-3 p-4 md:p-5">
          {sections.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.key} data-testid={`algo-section-${id}-${sec.key}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${sec.label}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                    {t.results.algorithms[sec.key]}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sec.items.map((item, i) => (
                    <span
                      key={i}
                      className={`inline-flex max-w-full rounded-lg border px-2.5 py-1.5 text-[11px] leading-snug md:text-xs ${sec.chip}`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="px-4 pb-4 text-xs text-zinc-400 md:px-5 md:pb-5">
          {t.results.algorithms.noRecs}
        </p>
      )}
    </motion.article>
  );
}
